import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserDataProvider, useUserData } from './contexts/UserDataContext';
import { GuardianProvider } from './contexts/GuardianContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import PublicResume from './pages/PublicResume';
import PublicResumeByUsername from './pages/PublicResumeByUsername';
import ContentPlanning from './pages/ContentPlanning';
import SchoolDirectory from './pages/SchoolDirectory';
import Communications from './pages/Communications';
import Settings from './pages/Settings';
import FAQ from './pages/FAQ';
import AcceptInvitation from './pages/AcceptInvitation';
import OnboardingFlow from './components/OnboardingFlow';
import Navbar from './components/Navbar';
import TrialBanner from './components/TrialBanner';

import './App.css';

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const { hasCompletedOnboarding, loadingOnboarding } = useUserData();

  if (!user) return <Navigate to="/" replace />;
  
  if (loadingOnboarding) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hasCompletedOnboarding) return <Navigate to="/onboarding" replace />;

  return children;
}

function OnboardingRoute({ children }) {
  const { user } = useAuth();
  const { hasCompletedOnboarding, loadingOnboarding } = useUserData();

  if (!user) return <Navigate to="/" replace />;

  if (loadingOnboarding) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (hasCompletedOnboarding) return <Navigate to="/dashboard" replace />;

  return children;
}

function AppContent() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {user && <TrialBanner />}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/resume/:athleteId" element={<PublicResume />} />
          <Route path="/:username" element={<PublicResumeByUsername />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
          
          <Route 
            path="/onboarding" 
            element={
              <OnboardingRoute>
                <OnboardingFlow />
              </OnboardingRoute>
            } 
          />
          
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/content-planning" 
            element={
              <ProtectedRoute>
                <ContentPlanning />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/schools" 
            element={
              <ProtectedRoute>
                <SchoolDirectory />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/communications" 
            element={
              <ProtectedRoute>
                <Communications />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </motion.div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <UserDataProvider>
          <GuardianProvider>
            <SubscriptionProvider>
              <AppContent />
            </SubscriptionProvider>
          </GuardianProvider>
        </UserDataProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;